"use client";

import { Template } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Image as ImageIcon, Video, Camera } from "lucide-react";
import Image from "next/image";
import { memo, useMemo, useState, useRef, useEffect } from "react";
import { getTemplateLibraryMeta } from "@/lib/template-library";

interface TemplateCardProps {
  template: Template;
  onPreview: (template: Template) => void;
  onUse: (template: Template) => void;
  onModelClick?: (modelId: string) => void;
  className?: string;
}

function TemplateCardImpl({ template, onPreview, onUse, onModelClick, className }: TemplateCardProps) {
  const libraryMeta = useMemo(() => getTemplateLibraryMeta(template), [template]);
  const isEffect = (template.type || "").toLowerCase() === "motion";
  const isVideo = (template.type || "").toLowerCase() === "video" || isEffect;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const isVideoUrl = (url: string) => /\.(mp4|webm|mov)(\?|$)/i.test(url);

  // Only treat a URL as a video if the extension actually says so.
  // Motion/effect covers are often static .webp/.png — rendering them inside
  // a <video> element wastes mounts, spams decode errors and can freeze the
  // browser when a shelf holds 100+ items.
  const videoSrc = useMemo(() => {
    const preview = template.preview_url || "";
    const cover = template.cover_url || "";
    if (isVideoUrl(preview)) return preview;
    if (isVideoUrl(cover)) return cover;
    return "";
  }, [template.preview_url, template.cover_url]);
  const hasVideoPreview = isVideo && !!videoSrc;

  // Poster image: use non-video URL so browsers can display it as a static thumbnail
  const imageSrc = useMemo(() => {
    const cover = template.cover_url || "";
    const preview = template.preview_url || "";
    if (cover && !isVideoUrl(cover)) return cover;
    if (preview && !isVideoUrl(preview)) return preview;
    return "";
  }, [template.cover_url, template.preview_url]);

  // Lazy video autoplay for effects and video templates with .mp4 preview.
  // No React state — `isVisible` was being set on every IntersectionObserver
  // callback even though nothing read it, which on mobile re-rendered every
  // card on scroll and made the showcase visibly «flicker». Just play/pause
  // the imperative element ref instead.
  useEffect(() => {
    if (!hasVideoPreview) return;
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) videoRef.current?.play().catch(() => {});
      else videoRef.current?.pause();
    }, { rootMargin: "100px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasVideoPreview]);

  const modelLabel = useMemo(() => {
    if (!libraryMeta.modelLabel) return null;
    const resolved = libraryMeta.modelLabel;
    const normalizedTitle = (template.title || "").trim().toLowerCase();
    const normalizedResolved = (resolved || "").trim().toLowerCase();
    if (normalizedTitle && normalizedResolved === normalizedTitle) return null;
    return resolved;
  }, [libraryMeta.modelLabel, template.title]);

  const typeLabel = useMemo(() => {
    switch ((template.type || "").toLowerCase()) {
      case "video": return "Видео";
      case "image": return "Фото";
      case "motion": return "Эффект";
      case "photoshoot": return "Фотосет";
      default: return template.type;
    }
  }, [template.type]);

  const promptText = typeof template.prompt === "string"
    ? template.prompt
    : template.prompt?.base || "";
  const isJsonPrompt = promptText.trimStart().startsWith("{") || promptText.trimStart().startsWith("[");
  const hasRealPrompt = promptText.length > 0 && !promptText.includes("{prompt}") && !isEffect && !isJsonPrompt;

  return (
    <div
      ref={cardRef}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/20 cursor-pointer",
        className
      )}
      onClick={() => onPreview(template)}
    >
      {/* Image / Video Area */}
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted/20">
        {hasVideoPreview && !videoFailed ? (
          /* Video preview for effects and video templates */
          <video
            ref={videoRef}
            src={videoSrc}
            poster={imageSrc || undefined}
            className="absolute inset-0 w-full h-full object-cover"
            muted
            loop
            playsInline
            preload={imageSrc ? "metadata" : "auto"}
            onError={() => setVideoFailed(true)}
          />
        ) : imageSrc ? (
          <Image
            src={imageSrc}
            alt={template.title}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width: 768px) 50vw, 33vw"
            unoptimized
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center text-muted-foreground/30 bg-secondary/30">
            <ImageIcon className="h-12 w-12 opacity-50" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

        {/* Badges (top) */}
        <div className="absolute top-2 left-2 right-2 z-10 flex items-center gap-1.5">
          <Badge variant="secondary" className="bg-black/50 text-white backdrop-blur-sm border-0 px-2 py-0.5 text-[10px] font-medium">
            {typeLabel}
          </Badge>
          {modelLabel && !isEffect && (
            <Badge variant="secondary" className="bg-white/10 text-white/70 backdrop-blur-sm border-0 px-2 py-0.5 text-[10px]">
              {modelLabel}
            </Badge>
          )}
        </div>

        {/* Bottom overlay content */}
        <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
          <h3 className="font-semibold text-[13px] leading-tight text-white line-clamp-2 drop-shadow-sm">
            {template.title}
          </h3>
        </div>
      </div>
    </div>
  );
}

// Memoize: PromptLibraryShowcase passes a stable templates array but parent
// LibraryView re-renders on every filter-state tick. Without memo each tick
// re-rendered all 70+ cards across 9 shelves on mobile, which compounded with
// the (now-removed) isVisible state churn into the «бесконечно дрыгаются»
// flicker the user reported.
export const TemplateCard = memo(TemplateCardImpl);
